import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import classnames from 'classnames';

import { Text, TextType } from 'ui-components/components/text';

import { USD } from '../../../constants/currency-codes';
import Global from '../../../store/global-selectors';
import { MarketplaceIds } from '../../../enums/marketplace-ids';
import { BASE_STORE_SETUP_URL } from '../../../routes/constants';
import { KeyMetrics } from '../components/key-metrics';
import FormattedCurrencyContainer from '../../../components/formatted-currency/container';
import NotificationEmailsGraph from '../components/graphs/notification-email-graph';
import { MostValuableCustomerTable } from '../components/most-valuable-customer-table';
import OrderByRegionGraph from '../components/graphs/order-by-region-graph';
import { SalesByStoreTable } from '../components/sales-by-store-table';
import { SalesByCategoryTable } from '../components/sales-by-category-table';
import { TopFiveProductsByRevenueTable } from '../components/top-five-products-by-revenue-table';
import NumItemsSoldOverTimeGraph from '../components/graphs/num-items-sold-over-time-graph';
import InsufficientDataBannerMessage from '../components/insufficient-data-banner-message';
import { doubleDash } from '../constants';

import messages from './messages';
import style from './style.module.less';
import analyticsStyle from '../analytics-style.module.less';

export const AnalyticsSalesAndCustomers = ({ chartData, selectedStore }) => {
  const {
    orderByInterval,
    orderByStore,
    customerSummary,
    productByCategoryCount,
    productByAmount,
    email,
    deliveryEmail,
    byCountry,
    topSpendingCustomers,
  } = chartData || {};
  const { newCustomers, returnCustomers, totalOrderAmount, totalOrderAmountPrev, avgOrderAmount, totalOrderCount } = customerSummary || {};
  const dataIsUndefined = newCustomers === undefined;
  const activeStoresResult = useSelector(Global.selectSellerActiveStores);
  const filteredStores = useMemo(
    () => (activeStoresResult || []).filter((store) => store.marketplaceId !== MarketplaceIds.AuctaneFulfillment),
    [activeStoresResult],
  );
  const { shouldShowShipmentNotificationChart, shouldShowDeliveryNotificationChart } = useMemo(
    () => ({
      shouldShowShipmentNotificationChart: selectedStore
        ? !!selectedStore?.shipmentEmailTemplate
        : filteredStores.some((store) => store?.shipmentEmailTemplate),
      shouldShowDeliveryNotificationChart: selectedStore
        ? !!selectedStore?.deliveryEmailTemplate
        : filteredStores.some((store) => store?.deliveryEmailTemplate),
    }),
    [selectedStore, filteredStores],
  );
  const storeSetupLink = selectedStore ? `${BASE_STORE_SETUP_URL}/${selectedStore.storeGuid}` : BASE_STORE_SETUP_URL;

  const defaultCurrency = useSelector(Global.selectSellerDefaultCurrency) || USD;

  const keyMetricsData = useMemo(
    () => [
      {
        id: 1,
        title: <FormattedMessage {...messages.salesSummary} values={{ currency: defaultCurrency }} />,
        value: dataIsUndefined ? doubleDash : <FormattedCurrencyContainer currencyCode={defaultCurrency} value={totalOrderAmount} />,
      },
      {
        id: 2,
        title: <FormattedMessage {...messages.newCustomers} />,
        value: dataIsUndefined ? doubleDash : <FormattedNumber value={newCustomers} />,
      },
      {
        id: 3,
        title: <FormattedMessage {...messages.salesVsTimeLastYear} />,

        value:
          dataIsUndefined || !totalOrderAmountPrev ? (
            doubleDash
          ) : (
            <FormattedCurrencyContainer currencyCode={defaultCurrency} value={totalOrderAmountPrev} />
          ),
      },
      {
        id: 4,
        title: <FormattedMessage {...messages.averageOrderValue} values={{ currency: defaultCurrency }} />,
        value: dataIsUndefined ? doubleDash : <FormattedCurrencyContainer currencyCode={defaultCurrency} value={avgOrderAmount} />,
      },
      {
        id: 5,
        title: <FormattedMessage {...messages.returningCustomers} />,
        value: dataIsUndefined ? doubleDash : <FormattedNumber value={returnCustomers} />,
      },
      {
        id: 6,
        title: <FormattedMessage {...messages.orders} />,
        value: dataIsUndefined ? doubleDash : <FormattedNumber value={totalOrderCount} />,
      },
    ],
    [
      dataIsUndefined,
      defaultCurrency,
      totalOrderAmount,
      newCustomers,
      totalOrderAmountPrev,
      avgOrderAmount,
      returnCustomers,
      totalOrderCount,
    ],
  );

  return (
    <>
      <KeyMetrics data={keyMetricsData} />
      {!customerSummary?.totalOrderCount && <InsufficientDataBannerMessage />}
      <div className={classnames(style['items-sold-over-time-graph'], analyticsStyle['analytics-section-wrapper'])}>
        <NumItemsSoldOverTimeGraph data={orderByInterval} shouldShowNullOverlay={!customerSummary?.totalOrderCount} />
      </div>

      <OrderByRegionGraph data={byCountry} isShipStationAnalyticsFeatureEnabled />

      <div className={classnames(style.container, analyticsStyle['analytics-container'])}>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <Text className={analyticsStyle['margin-24']} textType={TextType.H4} value={messages.shipmentNotificationEmailsTitle} />
          {shouldShowShipmentNotificationChart ? (
            <NotificationEmailsGraph data={email} store={selectedStore} />
          ) : (
            <Text
              className={style['helper-message']}
              textType={TextType.Paragraph}
              value={
                <FormattedMessage
                  {...messages.turnOnShipmentNotificationEmailsMessage}
                  values={{
                    link: (chunks) => <Link to={storeSetupLink}>{chunks}</Link>,
                  }}
                />
              }
            />
          )}
        </div>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <Text className={analyticsStyle['margin-24']} textType={TextType.H4} value={messages.deliveryNotificationEmailsTitle} />
          {shouldShowDeliveryNotificationChart ? (
            <NotificationEmailsGraph data={deliveryEmail} store={selectedStore} />
          ) : (
            <Text
              className={style['helper-message']}
              textType={TextType.Paragraph}
              value={
                <FormattedMessage
                  {...messages.turnOnDeliveryNotificationEmailsMessage}
                  values={{
                    link: (chunks) => <Link to={storeSetupLink}>{chunks}</Link>,
                  }}
                />
              }
            />
          )}
        </div>
      </div>

      <div className={classnames(style.container, analyticsStyle['analytics-container'])}>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <SalesByStoreTable data={orderByStore} />
        </div>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <SalesByCategoryTable data={productByCategoryCount} />
        </div>
      </div>

      <div className={classnames(style.container, analyticsStyle['analytics-container'])}>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <MostValuableCustomerTable data={topSpendingCustomers} />
          <p>
            <Link to='/dashboard/reports/Customer'>
              <FormattedMessage {...messages.customerDataLink} />
            </Link>
          </p>
        </div>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <TopFiveProductsByRevenueTable data={productByAmount} />
          <p>
            <Link to='/dashboard/reports/ProductSales'>
              <FormattedMessage {...messages.productSalesDataLink} />
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};
